import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import hoteles from "assets/HotelesNacionales.json";
import {
  Clock,
  ChevronLeft,
  ChevronRight,
  X,
} from "lucide-react";
import "./HotelDetail.css";
import { ICONS_MAP } from "assets/icons/iconsMap";
import { ReactComponent as LogoSVG } from "assets/patterns/eliteVacances.svg";
import { ReactComponent as WallpaperVG } from "assets/patterns/Textura.svg";
const HotelDetail = () => {
  const { destino, hotel } = useParams();
  const navigate = useNavigate();

  const [selectedHotel, setSelectedHotel] = useState(null);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxImage, setLightboxImage] = useState("");

  /* =============================
     Buscar hotel desde el JSON
  ============================== */
  useEffect(() => {
    const foundHotel = hoteles.HotelesNacionales.find(
      (h) => h.Destino === destino && h.Hotel === hotel
    );
    setSelectedHotel(foundHotel);
  }, [destino, hotel]);

  /* Reiniciar sliders al cambiar hotel */
  useEffect(() => {
    setCurrentSlide(0);
  }, [selectedHotel]);

  if (!selectedHotel) return null;

  /* =============================
     Handlers
  ============================== */
  const nextSlide = () =>
    setCurrentSlide((prev) => (prev + 1) % selectedHotel.slider.length);

  const prevSlide = () =>
    setCurrentSlide(
      (prev) =>
        (prev - 1 + selectedHotel.slider.length) % selectedHotel.slider.length
    );

  const openLightbox = (img) => {
    setLightboxImage(img);
    setLightboxOpen(true);
  };

  /* =============================
     JSX
  ============================== */
  return (
    <div className="hotel-detail">
      {/* ================= HERO ================= */}
      <section className="hero-section">
        <div className="hero-slider">
          <img
            src={selectedHotel.slider[currentSlide].img}
            alt={selectedHotel.Hotel}
          />
          <div className="hero-overlay" />

          <div className="hero-content">
            {/* <span className="hero-subtitle">THE CARIBBEAN ESCAPE</span> */}
            <h1 className="hero-title">Hotel {selectedHotel.Hotel}</h1>
            <p className="hero-location">{selectedHotel.Destino}</p>
            {/* <p className="hero-description">{selectedHotel.descripcion}</p> */}

            {selectedHotel.noches && (
              <button className="hero-cta">Redime tus noches</button>
            )}
          </div>

          <button className="slider-btn prev" onClick={prevSlide}>
            <ChevronLeft size={32} />
          </button>
          <button className="slider-btn next" onClick={nextSlide}>
            <ChevronRight size={32} />
          </button>

          <div className="slider-dots">
            {selectedHotel.slider.map((_, idx) => (
              <span
                key={idx}
                className={`dot ${idx === currentSlide ? "active" : ""}`}
                onClick={() => setCurrentSlide(idx)}
              />
            ))}
          </div>
        </div>
      </section>

      {/* ================= ABOUT ================= */}
      <section className="about-section">
        <div className="container about-grid">
          <div className="about-text">
            <span className="section-label">Acerca del hotel</span>
            <h2 className="section-title-hotel-detail">
              Hotel {selectedHotel.Hotel}
            </h2>
            <p className="section-description-hotel-detail">
              {selectedHotel.descripcion}
            </p>

            <div className="amenities-grid">
              {selectedHotel.iconos_servicios?.map((servicio, idx) => {
                const iconName = servicio.icon.split("/").pop();
                const Icon = ICONS_MAP[iconName];

                return (
                  <div key={idx} className="amenity-item">
                    {Icon && <Icon className="amenity-icon" />}
                    <span>{servicio.name}</span>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="about-image-wrapper">
            <div className="about-image">
              <div className="border-top-left"></div>
              <div className="border-bottom-right"></div>
              <img
                src={selectedHotel.slider[currentSlide].img}
                alt={selectedHotel.Hotel}
              />
            </div>
          </div>
        </div>
      </section>

      {/* ================= ROOMS ================= */}
      <section className="accommodations-section">
        <div className="container">
          <div className="section-header-hotel-detail">
            <span className="section-label">Habitaciones</span>
          </div>

          <div className="rooms-grid">
            {selectedHotel.habitaciones.map((room, idx) => (
              <div key={idx} className="room-card">
                <img
                  src={room.img}
                  alt={`Habitación ${idx + 1}`}
                  onClick={() => openLightbox(room.img)}
                />
                <div className="room-info">
                  <article>
                    <p>{selectedHotel.Hotel}</p>
                    <p className="hero-location">{selectedHotel.Destino}</p>
                  </article>
                  <LogoSVG height={20} />

                  {/* <h3>{room.nombre || "Habitación"}</h3> */}
                  <p>
                    {/* {room.descripcion || "Diseñada para descanso y confort"} */}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ================= DESTINATION ================= */}
      <section className="destination-section">
        <WallpaperVG className="bg-svg-icon" />
        <div className="container">
          <span className="section-label">GUÍA DE DESTINO</span>
          <h2 className="section-title-hotel-detail">
            {selectedHotel.Destino}
          </h2>

          <div className="destination-grid">
            {selectedHotel.detalles?.map((detalle, idx) => {
              const key = Object.keys(detalle)[0];
              const value = detalle[key];

              return (
                <div key={idx} className="destination-info">
                  <span className="info-label">{key}</span>
                  <p className="info-value">{value}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ================= PLAN ================= */}
      <section className="plan-section">
        <div className="container plan-grid">
          <div className="plan-info">
            <h2 className="section-title-hotel-detail">
              Planifique su estancia
            </h2>

            <div className="schedule-info">
              <div className="schedule-item">
                <Clock size={20} />
                <div>
                  <span>Check-in</span>
                  <p>{selectedHotel.horarios[0].check_in}</p>
                </div>
              </div>

              <div className="schedule-item">
                <Clock size={20} />
                <div>
                  <span>Check-out</span>
                  <p>{selectedHotel.horarios[0].check_out}</p>
                </div>
              </div>
            </div>
            {selectedHotel.zonas && (
              <div className="attractions">
                <h3>Sitios de interés</h3>
                <div
                  dangerouslySetInnerHTML={{ __html: selectedHotel.zonas }}
                />
              </div>
            )}
          </div>

          <div className="plan-map">
            <iframe
              src={selectedHotel.map}
              width="100%"
              height="100%"
              style={{ border: 0 }}
              loading="lazy"
              title={selectedHotel.Hotel}
            />
          </div>
        </div>
      </section>

      {/* ================= LIGHTBOX ================= */}
      {lightboxOpen && (
        <div className="lightbox" onClick={() => setLightboxOpen(false)}>
          <button className="lightbox-close">
            <X size={32} />
          </button>
          <img src={lightboxImage} alt="Preview" />
        </div>
      )}

      {/* ================= BACK ================= */}
      <button className="back-btn" onClick={() => navigate(-1)}>
        ← Volver
      </button>
    </div>
  );
};

export default HotelDetail;
